import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import {
  FaHome, FaMoneyBillWave, FaLink, FaChartLine, FaUserCircle, FaSignOutAlt,
  FaCopy, FaCheckCircle, FaWallet, FaHandshake, FaQuestionCircle,
  FaTrophy, FaArrowRight, FaLock, FaSave, FaSync,
  FaMousePointer, FaUniversity
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

// --- COMPOSANT STAT CARD ---
const StatCard = ({ title, value, icon: Icon, color }: any) => (
  <div className={`bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm relative overflow-hidden group hover:shadow-lg transition-all duration-300`}>
    <div className={`absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity text-${color}-500`}>
      <Icon size={50} />
    </div>
    <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-2">{title}</p>
    <p className="text-3xl font-black text-slate-900 flex items-center gap-2">
      {value}
      <span className={`text-${color}-500 text-sm bg-${color}-50 p-1 rounded-full`}>
        <Icon />
      </span>
    </p>
  </div>
);

// --- COMPOSANT FAQ ITEM ---
const FaqItem = ({ question, answer }: { question: string, answer: string }) => (
    <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100">
        <h4 className="font-bold text-slate-900 flex items-center gap-2 mb-2">
            <FaQuestionCircle className="text-purple-500"/> {question}
        </h4>
        <p className="text-sm text-slate-600 leading-relaxed">{answer}</p>
    </div>
);

export default function AffiliateDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // ✅ AJOUT DE 'faq' DANS LES ONGLETS
  const [activeTab, setActiveTab] = useState<'overview' | 'tools' | 'earnings' | 'settings' | 'faq'>('overview');
  
  const [affiliate, setAffiliate] = useState<any>(null);
  const [user, setUser] = useState<any>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [payouts, setPayouts] = useState<any[]>([]);

  const [customUrl, setCustomUrl] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [passwordState, setPasswordState] = useState({ new: '', confirm: '' });

  useEffect(() => {
    fetchAffiliateData();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe_connect') === 'success') {
        alert("🎉 Compte bancaire connecté avec succès !");
        window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const fetchAffiliateData = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate('/login'); return; }
      setUser(session.user);

      let { data: affData } = await supabase.from('affiliates').select('*').eq('user_id', session.user.id).maybeSingle();
      
      if (affData) {
        setAffiliate(affData);
        setGeneratedLink(`${window.location.origin}/?ref=${affData.referral_code}`);

        const { data: transData } = await supabase.from('affiliate_transactions').select('*').eq('affiliate_id', affData.id).order('created_at', { ascending: false });
        setTransactions(transData || []);

        const { data: payData } = await supabase.from('affiliate_payouts').select('*').eq('affiliate_id', affData.id).order('created_at', { ascending: false });
        setPayouts(payData || []);
      }
    } catch (error) {
      console.error("Erreur chargement", error);
    } finally {
      setLoading(false);
    }
  };

  const totalEarnings = transactions.reduce(
    (sum, transaction) =>
      sum + Number(transaction.amount || 0),
    0,
  );
  const totalPaidOut = payouts.filter(p => p.status !== 'rejected').reduce((sum, p) => sum + Number(p.amount), 0);
  const currentBalance = totalEarnings - totalPaidOut;

  const handleGenerateLink = () => {
    let baseUrl = customUrl.trim() || window.location.origin;
    if (baseUrl.startsWith('/')) baseUrl = window.location.origin + baseUrl;
    if (!baseUrl.startsWith('http')) baseUrl = 'https://' + baseUrl;

    try {
        const url = new URL(baseUrl);
        url.searchParams.set('ref', affiliate?.referral_code);
        setGeneratedLink(url.toString());
    } catch {
        alert("URL invalide");
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    alert("Lien copié ! 🚀");
  };

  const handleConnectStripe = async () => {
    setSaving(true);
    try {
        const { data: { session } } = await supabase.auth.getSession();
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-connect`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${session?.access_token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                affiliate_id: affiliate.id,
                user_type: 'affiliate',
                return_url: window.location.href
            })
        });

        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || "Erreur serveur Stripe");
        }

        if (data.url) {
            window.location.href = data.url;
        } else {
            throw new Error("Aucune URL de redirection reçue de Stripe.");
        }
    } catch (err: any) {
        alert("Erreur Stripe : " + err.message);
    } finally {
        setSaving(false);
    }
  };

  const handleRequestPayout = async () => {
    if (currentBalance < 50) {
      return alert(
        "Le solde minimum pour un retrait est de 50$."
      );
    }

    if (!affiliate?.stripe_account_id) {
      return alert(
        "Veuillez d'abord connecter votre compte bancaire."
      );
    }

    if (!confirm(
      `Confirmer la demande de retrait disponible ?`
    )) {
      return;
    }

    setSaving(true);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error("Session absente");
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/request-payout`,
        {
          method: 'POST',
          headers: {
            Authorization:
              `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            payout_type: 'affiliate',
          }),
        },
      );

      const result = await response.json();

      if (!response.ok || !result?.ok) {
        throw new Error(
          result?.error ||
          "Erreur lors de la demande"
        );
      }

      const amount =
        Number(result.amount_cents || 0) / 100;

      alert(
        `✅ Demande de ${amount.toFixed(2)}$ envoyée !`
      );

      fetchAffiliateData();
    } catch (err: any) {
      alert(
        "Erreur : " +
        (err?.message || "Demande impossible")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordState.new !== passwordState.confirm) return alert("Mots de passe différents.");
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password: passwordState.new });
    if (!error) { alert("Mot de passe mis à jour !"); setPasswordState({ new: '', confirm: '' }); } 
    else alert("Erreur : " + error.message);
    setSaving(false);
  };

  const handleLogout = async () => { await supabase.auth.signOut(); navigate('/login'); };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-spin rounded-full h-12 w-12 border-b-4 border-purple-600"></div></div>;

  return (
    <div className="min-h-screen bg-slate-50 font-sans flex flex-col text-slate-800">
      <Header />
      
      {/* Conteneur principal avec padding-bottom géant pour mobile */}
      <div className="flex flex-grow container mx-auto px-4 py-8 gap-6 pt-24 pb-32 md:pb-8">
        
        {/* SIDEBAR DESKTOP */}
        <aside className="hidden md:flex flex-col w-72 bg-white rounded-[2.5rem] shadow-2xl border border-slate-100 overflow-hidden h-[calc(100vh-8rem)] sticky top-28">
          <div className="p-8 bg-gradient-to-br from-purple-50 to-pink-50 border-b border-purple-100 text-center relative overflow-hidden">
             <div className="absolute top-[-20px] right-[-20px] w-24 h-24 bg-purple-200 rounded-full blur-2xl opacity-50"></div>
             <div className="w-20 h-20 bg-white rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg text-purple-600 text-3xl border-4 border-white relative z-10"><FaUserCircle /></div>
             <h2 className="font-black text-lg truncate relative z-10 text-slate-900">{user?.user_metadata?.full_name || "Partenaire"}</h2>
             <span className="inline-block mt-2 px-3 py-1 bg-purple-600 text-white text-[10px] font-black uppercase tracking-widest rounded-full shadow-sm relative z-10">
                {affiliate?.status === 'active' ? 'Actif' : 'En attente'}
             </span>
          </div>
          <nav className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
            {[
              { id: 'overview', icon: <FaHome />, label: 'Tableau de bord' },
              { id: 'tools', icon: <FaLink />, label: 'Générateur de Liens' },
              { id: 'earnings', icon: <FaWallet />, label: 'Gains & Retraits' },
              { id: 'faq', icon: <FaQuestionCircle />, label: 'FAQ & Aide' }, // ✅ Ajout Menu
              { id: 'settings', icon: <FaHandshake />, label: 'Paramètres' },
            ].map(item => (
              <button key={item.id} onClick={() => setActiveTab(item.id as any)} className={`w-full flex items-center justify-between px-5 py-4 rounded-2xl text-sm font-bold transition-all group ${activeTab === item.id ? 'bg-slate-900 text-white shadow-xl scale-105' : 'text-slate-500 hover:bg-purple-50 hover:text-purple-600'}`}>
                <div className="flex items-center gap-4"><span className={`text-lg ${activeTab === item.id ? 'text-purple-300' : 'text-slate-400'}`}>{item.icon}</span><span className="tracking-tight">{item.label}</span></div>
                {activeTab === item.id && <FaArrowRight className="text-xs opacity-50"/>}
              </button>
            ))}
          </nav>
          <div className="p-4 mt-auto border-t border-slate-100 bg-slate-50/50">
            <button onClick={handleLogout} className="w-full text-red-500 text-xs font-bold flex items-center justify-center gap-2 hover:bg-red-50 py-3 rounded-2xl transition"><FaSignOutAlt /> Déconnexion</button>
          </div>
        </aside>

        {/* ✅ BARRE DE NAVIGATION MOBILE */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-[9999] px-2 py-3 flex justify-around items-center shadow-[0_-4px_20px_rgba(0,0,0,0.1)] safe-area-bottom">
            {[
              { id: 'overview', icon: <FaHome size={20} />, label: 'Accueil' },
              { id: 'tools', icon: <FaLink size={20} />, label: 'Liens' },
              { id: 'earnings', icon: <FaWallet size={20} />, label: 'Gains' },
              { id: 'faq', icon: <FaQuestionCircle size={20} />, label: 'Aide' }, // ✅ Ajout Mobile
              { id: 'settings', icon: <FaHandshake size={20} />, label: 'Profil' },
            ].map(item => (
              <button 
                key={item.id} 
                onClick={() => setActiveTab(item.id as any)} 
                className={`flex flex-col items-center justify-center p-2 rounded-xl transition w-16 ${activeTab === item.id ? 'text-purple-600 bg-purple-50' : 'text-slate-400'}`}
              >
                <span className="mb-1">{item.icon}</span>
                <span className="text-[9px] font-black uppercase tracking-tight">{item.label}</span>
              </button>
            ))}
        </div>

        {/* MAIN CONTENT */}
        <main className="flex-1 min-w-0">
          
          {activeTab === 'overview' && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="bg-gradient-to-r from-slate-900 to-purple-900 p-10 rounded-[2.5rem] shadow-2xl text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500 rounded-full blur-[80px] opacity-20 -mr-16 -mt-16"></div>
                <div className="relative z-10">
                    <h1 className="text-3xl md:text-4xl font-black mb-2">Tableau de bord</h1>
                    <p className="text-purple-200 text-lg font-medium">Commission : <span className="text-white bg-white/20 px-2 py-0.5 rounded font-bold">{affiliate?.commission_percent}%</span></p>
                    <div className="mt-8 flex gap-4">
                        <button onClick={() => setActiveTab('tools')} className="bg-white text-slate-900 px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-purple-50 transition shadow-lg flex items-center gap-2"><FaLink className="text-purple-600"/> Créer un lien</button>
                        <div className="px-6 py-3 rounded-xl border border-white/20 text-xs font-bold uppercase tracking-widest text-purple-200 flex items-center gap-2">Code: <span className="text-white font-mono text-base">{affiliate?.referral_code}</span></div>
                    </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard title="Solde Actuel" value={`${currentBalance.toFixed(2)}$`} icon={FaWallet} color="green" />
                <StatCard title="Total Gagné" value={`${totalEarnings.toFixed(2)}$`} icon={FaMoneyBillWave} color="purple" />
                <StatCard title="Clics" value={affiliate?.clicks_count || 0} icon={FaMousePointer} color="blue" />
                <StatCard title="Ventes" value={affiliate?.conversions_count || 0} icon={FaTrophy} color="yellow" />
              </div>
            </div>
          )}

          {activeTab === 'tools' && (
            <div className="bg-white p-10 rounded-[2.5rem] shadow-lg border border-slate-100">
                <h2 className="text-2xl font-black text-slate-900 mb-6 flex items-center gap-3"><FaLink className="text-blue-500"/> Générateur de liens</h2>
                <div className="bg-slate-50 p-6 rounded-[2rem] border border-slate-200 space-y-4">
                    <div className="flex flex-col md:flex-row gap-2">
                        <input type="text" placeholder="ex: https://forfeo.com/place/hotel-xyz" value={customUrl} onChange={e => setCustomUrl(e.target.value)} className="flex-1 p-4 rounded-xl border border-slate-200 outline-none font-medium text-sm"/>
                        <button onClick={handleGenerateLink} className="bg-slate-900 text-white px-6 py-4 md:py-0 rounded-xl font-bold text-xs uppercase hover:bg-slate-800">Générer</button>
                    </div>
                    {generatedLink && <div className="flex flex-col md:flex-row gap-2"><input readOnly value={generatedLink} className="flex-1 bg-green-50 border border-green-200 text-green-900 font-bold p-4 rounded-xl text-sm outline-none"/><button onClick={handleCopyLink} className="bg-green-600 text-white px-6 py-4 md:py-0 rounded-xl font-black text-xs uppercase hover:bg-green-700 flex items-center justify-center gap-2"><FaCopy/> Copier</button></div>}
                </div>
            </div>
          )}

          {activeTab === 'earnings' && (
             <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1 bg-gradient-to-b from-slate-900 to-slate-800 text-white p-8 rounded-[2.5rem] shadow-xl flex flex-col justify-between h-96 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-white opacity-5 rounded-full -mr-10 -mt-10 blur-3xl"></div>
                    <div><p className="text-slate-400 text-xs font-black uppercase tracking-widest mb-1">Solde disponible</p><h2 className="text-5xl font-black tracking-tight">{currentBalance.toFixed(2)} $</h2></div>
                    <div>
                        <div className="bg-white/10 p-4 rounded-xl mb-6 border border-white/10">
                            <label className="text-[10px] uppercase font-bold text-slate-400 flex items-center gap-2 mb-2"><FaUniversity/> Compte Bancaire</label>
                            {affiliate?.stripe_account_id ? <div className="flex items-center gap-2 text-green-400 font-bold text-sm"><FaCheckCircle/> Connecté avec Stripe</div> : <button onClick={handleConnectStripe} disabled={saving} className="w-full bg-[#635BFF] text-white py-2 rounded-lg font-bold text-xs hover:bg-[#5851e3] transition flex items-center justify-center gap-2">{saving ? <FaSync className="animate-spin"/> : "Connecter mon compte"}</button>}
                        </div>
                        <button onClick={handleRequestPayout} disabled={saving || currentBalance < 50 || !affiliate?.stripe_account_id} className={`w-full py-4 rounded-2xl font-black uppercase tracking-widest text-xs flex items-center justify-center gap-2 shadow-lg transition ${(currentBalance < 50 || !affiliate?.stripe_account_id) ? 'bg-slate-700 text-slate-500 cursor-not-allowed' : 'bg-white text-slate-900 hover:bg-purple-50'}`}>{saving ? <FaSync className="animate-spin"/> : <FaWallet/>} Demander virement</button>
                    </div>
                </div>
                <div className="lg:col-span-2 bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100">
                    <h3 className="font-black text-slate-900 mb-6 flex items-center gap-2"><FaChartLine className="text-green-500"/> Historique</h3>
                    {payouts.length === 0 ? <p className="text-slate-400 font-bold text-sm">Aucune demande.</p> : (
                        <div className="overflow-x-auto"><table className="w-full text-left"><thead className="text-[10px] font-black uppercase text-slate-400 bg-slate-50 rounded-xl"><tr><th className="p-4 rounded-l-xl">Date</th><th className="p-4">Statut</th><th className="p-4 text-right rounded-r-xl">Montant</th></tr></thead><tbody className="text-sm font-medium text-slate-600">{payouts.map(p => (<tr key={p.id} className="border-b border-slate-50"><td className="p-4">{new Date(p.created_at).toLocaleDateString()}</td><td className="p-4">{p.status}</td><td className="p-4 text-right font-bold">-{Number(p.amount).toFixed(2)}$</td></tr>))}</tbody></table></div>
                    )}
                </div>
             </div>
          )}

          {/* ✅ NOUVELLE SECTION FAQ */}
          {activeTab === 'faq' && (
              <div className="space-y-8 animate-in fade-in">
                  <div className="bg-white p-10 rounded-[2.5rem] shadow-lg border border-slate-100">
                      <h2 className="text-2xl font-black text-slate-900 mb-6 flex items-center gap-2"><FaQuestionCircle className="text-purple-600"/> Foire aux questions</h2>
                      <div className="space-y-4">
                          <FaqItem 
                            question="Comment fonctionne le système de commission ?" 
                            answer="Vous gagnez 25% de commission sur chaque vente générée via votre lien unique. Le lien dépose un cookie actif pendant 30 jours. Si le client achète durant cette période, la vente vous est attribuée."
                          />
                          <FaqItem 
                            question="Quand puis-je demander un virement ?" 
                            answer="Vous pouvez demander un virement dès que votre solde disponible atteint 50.00 $ CAD. Le bouton 'Demander virement' dans l'onglet 'Gains' s'activera automatiquement."
                          />
                          <FaqItem 
                            question="Comment l'argent est-il envoyé ?" 
                            answer="Nous utilisons Stripe Connect. Une fois votre compte bancaire lié (onglet Gains > Connecter), les virements sont envoyés directement sur votre compte. Le délai bancaire est généralement de 2 à 5 jours ouvrables."
                          />
                          <FaqItem 
                            question="Puis-je créer des liens vers des pages spécifiques ?" 
                            answer="Oui ! Dans l'onglet 'Générateur de Liens', vous pouvez coller l'URL d'un hôtel ou d'une activité spécifique pour créer un lien direct qui contient votre code affilié."
                          />
                      </div>
                  </div>
              </div>
          )}

          {activeTab === 'settings' && (
              <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in">
                  <div className="bg-white p-10 rounded-[2.5rem] shadow-lg border border-slate-100">
                      <h2 className="text-2xl font-black text-slate-900 mb-6 flex items-center gap-2"><FaLock className="text-red-500"/> Sécurité</h2>
                      <form onSubmit={handleUpdatePassword} className="space-y-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div><label className="block text-xs font-black uppercase text-slate-400 mb-2">Nouveau mot de passe</label><input type="password" value={passwordState.new} onChange={(e) => setPasswordState({...passwordState, new: e.target.value})} className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-700 outline-none focus:border-red-500"/></div>
                              <div><label className="block text-xs font-black uppercase text-slate-400 mb-2">Confirmer</label><input type="password" value={passwordState.confirm} onChange={(e) => setPasswordState({...passwordState, confirm: e.target.value})} className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-700 outline-none focus:border-red-500"/></div>
                          </div>
                          <button type="submit" disabled={saving || !passwordState.new} className="bg-red-500 text-white px-8 py-4 rounded-2xl font-black uppercase tracking-widest hover:bg-red-600 transition w-full flex items-center justify-center gap-2 disabled:opacity-50">{saving ? <FaSync className="animate-spin"/> : <FaLock />} Mettre à jour</button>
                      </form>
                      <button onClick={handleLogout} className="md:hidden w-full mt-8 bg-slate-100 text-slate-900 px-8 py-4 rounded-2xl font-black uppercase tracking-widest hover:bg-slate-200 transition">Se déconnecter</button>
                  </div>
              </div>
          )}
        </main>
      </div>
      <Footer />
    </div>
  );
}
